import React, { useMemo, useCallback } from 'react';
import { KiteInput, KiteSelect } from '@kite/react-kite';
import './AddDeviceForm.scss';
import {
  IDIYProduct,
  ISpectrumProduct,
  TDeviceTypeData,
  TSpectrumData,
  TDiyData,
} from 'types';
import {
  TDeviceTypeDataKeys,
  TSpectrumDataKeys,
  TDiyDataKeys,
} from 'types/TcoDeviceSelection';
import { handleNumInputKeydown } from 'utils';
import NumberFormat from 'react-number-format';
import { tcoFormattedPrice } from 'utils/defaultsAndConstants/tcoConstants';
import classNames from 'classnames';

interface ISelectOption {
  optLabel: string;
  optValue: string;
}

interface IInputConfig<T> {
  name: T;
  label: string;
  type: 'text' | 'text-label' | 'number' | 'select' | 'number-dollar';
  options?: ISelectOption[];
  readOnly: boolean;
}

type TFormErrors = {
  id?: string;
  diyId?: string;
  deviceType?: string;
  quantity?: string;
  spectrumId?: string;
  monthlyCost?: string;
  installationCost?: string;
  diyDeviceType?: string;
  diyModel?: string;
  hardwareCost?: string;
  licensingCost?: string;
  deviceDescription?: string;
  licenseDescription?: string;
  licenseTerm?: string;
  licenseType?: string;
};

export interface AddDeviceFormProps {
  /** Device metadata values */
  deviceData: TDeviceTypeData;
  /** Spectrum product selections */
  spectrumData: TSpectrumData;
  /** Diy product selections */
  diyData: TDiyData;
  /** Change handler for form inputs */
  onChange: (
    type: 'deviceType' | 'spectrumProduct' | 'diyProduct',
    values?: {
      formattedValue: string;
      value: string;
      floatValue?: number;
    }
  ) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  spectrumProducts: ISpectrumProduct[];
  diyProducts: IDIYProduct[];
  /** Errors for any form inputs */
  formErrors: TFormErrors;
}

const AddDeviceForm = ({
  spectrumProducts,
  diyProducts,
  deviceData,
  spectrumData,
  diyData,
  onChange,
  formErrors,
}: AddDeviceFormProps) => {
  // =============================================
  // State
  // =============================================

  // =============================================
  // Helpers
  // =============================================

  // Input configurations for each section
  const deviceInfoInputs: IInputConfig<TDeviceTypeDataKeys>[] = useMemo(
    () => [
      {
        name: 'deviceType',
        label: 'Device Type',
        type: 'text',
        readOnly: false,
      },
      {
        name: 'quantity',
        label: 'Quantity',
        type: 'number',
        readOnly: false,
      },
    ],
    []
  );

  const getOptions = useCallback(() => {
    if (!spectrumProducts) return [];
    const optionValue = [
      ...spectrumProducts.map((p) => ({
        optLabel: p.name,
        optValue: p.id,
      })),
    ];

    return optionValue;
  }, [spectrumProducts]);

  const spectrumInputs: IInputConfig<TSpectrumDataKeys>[] = useMemo(
    () => [
      {
        name: 'spectrumId',
        label: 'Service Description',
        type: 'select',
        options: getOptions(),
        readOnly: false,
      },
      {
        name: 'monthlyCost',
        label: 'Monthly Cost',
        type: 'number-dollar',
        readOnly: false,
      },
      {
        name: 'installationCost',
        label: 'Installation Cost',
        type: 'number-dollar',
        readOnly: false,
      },
    ],
    [getOptions]
  );

  const diyInputs: IInputConfig<TDiyDataKeys>[] = useMemo(
    () => [
      {
        name: 'diyDeviceType',
        label: 'Device Type',
        type: 'text',
        readOnly: true,
      },
      {
        name: 'diyModel',
        label: 'Model',
        type: 'text',
        readOnly: true,
      },
      {
        name: 'deviceDescription',
        label: 'Device Description',
        type: 'text-label',
        readOnly: true,
      },
      {
        name: 'hardwareCost',
        label: 'Hardware Cost',
        type: 'text-label',
        readOnly: true,
      },
      {
        name: 'licenseType',
        label: 'License Type',
        type: 'text-label',
        readOnly: true,
      },
      {
        name: 'licenseTerm',
        label: 'License Term',
        type: 'text-label',
        readOnly: true,
      },
      {
        name: 'licenseDescription',
        label: 'License Description',
        type: 'text-label',
        readOnly: true,
      },
      {
        name: 'licensingCost',
        label: 'Licensing Cost',
        type: 'text-label',
        readOnly: true,
      },
    ],
    []
  );

  // =============================================
  // Render Methods
  // =============================================

  const generateProfileDataInputs = useCallback(() => {
    return deviceInfoInputs.map((input: IInputConfig<TDeviceTypeDataKeys>) => {
      const isNotEditable = input.readOnly;
      const onKeyDown =
        input.name === 'quantity' ? handleNumInputKeydown : undefined;

      return (
        <KiteInput
          id={input.name}
          className={isNotEditable ? 'diy-input' : ''}
          name={input.name}
          label={`${input.label}`}
          key={input.name}
          type={input.type as 'text' | 'number'}
          value={deviceData && deviceData[input.name]}
          min="1"
          onChange={
            !isNotEditable ? (e) => onChange('deviceType')(e) : undefined
          }
          onKeyDown={onKeyDown}
          placeholder="Enter Value"
          errorMessage={formErrors && formErrors[input.name]}
        />
      );
    });
  }, [deviceInfoInputs, deviceData, onChange, formErrors]);

  const generateFormInputs = useCallback(
    (
      inputs: IInputConfig<TSpectrumDataKeys>[] | IInputConfig<TDiyDataKeys>[],
      spectrumInput: boolean
    ) => {
      return inputs.map((input) => {
        const isNotEditable = input.readOnly;
        if (input.type === 'select') {
          const inputOptions = input.options?.map((option: ISelectOption) => {
            return (
              <option key={option.optValue} value={option.optValue}>
                {option.optLabel}
              </option>
            );
          });

          const value = spectrumInput
            ? spectrumData?.[input.name as TSpectrumDataKeys] || ''
            : diyData[input.name as TDiyDataKeys];

          return (
            <KiteSelect
              id={input.name}
              name={input.name}
              label={`${input.label}`}
              key={input.name}
              value={value.toString()}
              onChange={
                spectrumInput
                  ? (
                      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
                    ) => onChange('spectrumProduct')(e)
                  : (
                      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
                    ) => onChange('diyProduct')(e)
              }
              placeholder="Select One"
              errorMessage={formErrors && formErrors[input.name]}
              className="label-text-input"
              maxWidth="600px"
            >
              {inputOptions}
            </KiteSelect>
          );
        } else if (input.type === 'number' || input.type === 'text') {
          const value = spectrumInput
            ? spectrumData[input.name as TSpectrumDataKeys]
            : diyData?.[input.name as TDiyDataKeys] || '';
          return (
            <KiteInput
              id={input.name}
              name={input.name}
              label={`${input.label}`}
              key={input.name}
              type={input.type}
              value={value}
              onChange={
                !isNotEditable
                  ? onChange(spectrumInput ? 'spectrumProduct' : 'diyProduct')
                  : undefined
              }
              onKeyDown={handleNumInputKeydown}
              disabled={!spectrumData || !spectrumData.spectrumId}
              className={isNotEditable ? 'diy-input' : ''}
              errorMessage={formErrors && formErrors[input.name]}
            />
          );
        } else if (input.type === 'text-label') {
          const value = spectrumInput
            ? spectrumData && spectrumData[input.name as TSpectrumDataKeys]
            : diyData && diyData[input.name as TDiyDataKeys];
          return (
            <div
              className={
                input.name.includes('Description') ? 'label-text-input' : ''
              }
              key={input.name}
            >
              {input.label}
              {input.name.includes('Cost') ? (
                <div className="label-text-input-cost">
                  {value ? `$${value}` : `$0.00`}
                </div>
              ) : (
                <div className="label-text-input-value">{value}</div>
              )}
            </div>
          );
        } else if (input.type === 'number-dollar') {
          const value = spectrumInput
            ? spectrumData?.[input.name as TSpectrumDataKeys] || ''
            : diyData[input.name as TDiyDataKeys];

          return (
            <div key={input.name}>
              {input.label}
              <NumberFormat
                value={value ? tcoFormattedPrice(value) : ''}
                name={input.name}
                thousandSeparator={','}
                onChange={(
                  e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
                ) =>
                  onChange(spectrumInput ? 'spectrumProduct' : 'diyProduct')(e)
                }
                prefix={'$'}
                decimalScale={2}
                allowNegative={false}
                readOnly={!spectrumData || !spectrumData.spectrumId}
                isNumericString
                className={classNames({
                  'kite-form-control': true,
                  'kite-input__input': true,
                  'kite-form-control-danger':
                    formErrors &&
                    formErrors[input.name] &&
                    spectrumData.spectrumId,
                })}
              />
              {formErrors &&
                formErrors[input.name] &&
                spectrumData.spectrumId && (
                  <p className="kite-form-control-feedback">
                    {formErrors && formErrors[input.name]}
                  </p>
                )}
            </div>
          );
        }
        return null;
      });
    },
    [spectrumData, onChange, diyData, formErrors]
  );

  // =============================================
  // Return
  // =============================================

  return (
    <form className="add_device_form">
      {/* Device Info Section */}
      <fieldset className="form-section">
        <div className="add_device_form__input-container">
          {generateProfileDataInputs()}
        </div>
      </fieldset>

      {/* Spectrum Section */}
      <fieldset className="form-section">
        <h4 className="add-device-form_sub_title">SPECTRUM</h4>
        <div className="add_device_form__input-container">
          {generateFormInputs(spectrumInputs, true)}
        </div>
        <div className="form-section-cost">
          <div>
            Monthly Subtotal
            <div className="form-section-cost-value">
              $
              {tcoFormattedPrice(
                Number(deviceData?.quantity) * Number(spectrumData?.monthlyCost)
              )}
            </div>
          </div>
          <div>
            Installation Subtotal
            <div className="form-section-cost-value">
              $
              {tcoFormattedPrice(
                Number(deviceData?.quantity) *
                  Number(spectrumData?.installationCost)
              )}
            </div>
          </div>
        </div>
      </fieldset>

      {/* DIY Section */}
      <fieldset className="form-section">
        <h1 className="add-device-form_sub_title">DIY</h1>
        <div className="add_device_form__input-container">
          {generateFormInputs(diyInputs, false).slice(0, 4)}
        </div>
        <div className="form-section-diy-cost">
          Hardware Subtotal
          <div className="form-section-diy-cost-value">
            $
            {tcoFormattedPrice(
              Number(deviceData?.quantity) * Number(diyData?.hardwareCost)
            )}
          </div>
        </div>
        <br />
        <hr />
        <br />
        <div className="add_device_form__input-container">
          {generateFormInputs(diyInputs, false).slice(4)}
        </div>
        <div className="form-section-cost">
          <div>
            Licensing Subtotal
            <div className="form-section-cost-value">
              $
              {tcoFormattedPrice(
                Number(deviceData?.quantity) * Number(diyData?.licensingCost)
              )}
            </div>
          </div>
        </div>
      </fieldset>
    </form>
  );
};

export default AddDeviceForm;
